/**
 * Owns connector operations for the selected workspace.
 *
 * Vault remains storage-only. This page composes provider-neutral analysis,
 * pull, and configuration entry points so connector implementations can grow
 * behind their shared contracts without changing application dashboards.
 */
import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Info, Plus } from 'lucide-react'
import { Navigate } from 'react-router-dom'

import { useServices } from '@/app-shell/services'
import { useWorkspaceConfig } from '@/app-shell/hooks'
import { useUserProfile } from '@/subsystems/workspaces/hooks/use-user-profile'
import {
  Button,
  Card,
  CardContent,
} from '@/components/ui'
import { ConnectorAnalysisPanel } from '@/applications/contract-review/components/connector-analysis/ConnectorAnalysisPanel'
import { workspaceDocumentsQueryKey } from '@/applications/contract-review/api/workspaceDocumentsQuery'
import { ociDashboardRecordsQueryKey } from '@/applications/contract-review/components/oci-dashboard/ociDashboardApi'
import { ConnectorPickerDialog } from '@/subsystems/vault/connectors/components/ConnectorPickerDialog'
import { ManageRecordsDialog } from '@/subsystems/vault/connectors/components/ManageRecordsDialog'
import { useWorkspaceConnectorControls } from '@/subsystems/vault/connectors/useWorkspaceConnectorControls'
import {
  getConnectorPullCatalog,
  refreshSyncedConnectorFeed,
  syncConnectorFeed,
} from '@/subsystems/vault/connectors/api'
import type { ConnectorPendingAction } from '@/subsystems/vault/connectors/types'
import { getWorkspaceVaultPairing, listVaults } from '@/subsystems/vault/services/vault-api'
import type { WorkspaceVaultPairingSummary } from '@/subsystems/vault/types'
import { useAuth } from '@/subsystems/workspaces'

function WorkspaceConnectorManager({
  orgId,
  workspaceId,
  pairing,
}: {
  orgId: string
  workspaceId: string
  pairing: WorkspaceVaultPairingSummary
}) {
  const api = useServices().getClient()
  const queryClient = useQueryClient()
  const { isGlobalAdmin, isWorkspaceAdmin, userId } = useAuth()
  const { data: userProfile } = useUserProfile()
  const myConnectorGrants = userProfile?.connectorAdminGrants ?? []
  const hasConnectorGrantInOrg = myConnectorGrants.some(
    grant => grant.orgId === orgId,
  )
  const [pullFeedId, setPullFeedId] = useState<string>()
  const [pendingActions, setPendingActions] = useState<Record<string, ConnectorPendingAction>>({})
  const [connectorPickerOpen, setConnectorPickerOpen] = useState(false)
  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['connector-analyses', orgId, workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['connector-pull-catalog', orgId, pairing.vaultId] }),
      queryClient.invalidateQueries({ queryKey: ['connector-document-mapping', orgId, pairing.vaultId] }),
      queryClient.invalidateQueries({
        queryKey: workspaceDocumentsQueryKey(orgId, workspaceId),
      }),
      // cm-coding: Saving dashboard fields changes the order stored on existing
      // documents. Refetch the OCI records query even when its page is inactive
      // so returning administrators cannot see or export the field order that
      // the completed save just replaced.
      queryClient.invalidateQueries({
        queryKey: ociDashboardRecordsQueryKey(orgId, workspaceId),
        refetchType: 'all',
      }),
    ])
  }, [orgId, pairing.vaultId, queryClient, workspaceId])
  const controls = useWorkspaceConnectorControls({
    api,
    orgId,
    vaultId: pairing.vaultId,
    pairing,
    canConfigure: isGlobalAdmin || isWorkspaceAdmin || hasConnectorGrantInOrg,
    canAdministerWorkspace: isGlobalAdmin || isWorkspaceAdmin,
    userId,
    refresh,
  })
  const vaults = useQuery({
    queryKey: ['vault-catalog', orgId],
    queryFn: () => listVaults(api, orgId),
  })
  const pullCatalog = useQuery({
    queryKey: ['connector-pull-catalog', orgId, pairing.vaultId],
    queryFn: () => getConnectorPullCatalog(api, orgId, pairing.vaultId),
  })
  const vaultName = vaults.data?.find(entry => entry.vault.vaultId === pairing.vaultId)?.vault.name
  const setPendingAction = useCallback((feedId: string, action?: ConnectorPendingAction) => {
    setPendingActions((current) => {
      const next = { ...current }
      if (action) next[feedId] = action
      else delete next[feedId]
      return next
    })
  }, [])

  const syncFeed = async (feedId: string) => {
    const response = await syncConnectorFeed(api, orgId, pairing.vaultId, feedId)
    await refresh()
    return response.jobId
  }

  const refreshSyncedRecords = async (feedId: string) => {
    const response = await refreshSyncedConnectorFeed(
      api,
      orgId,
      pairing.vaultId,
      feedId,
    )
    await refresh()
    return response.jobId
  }

  const addConnectorButton = () => (
    <Button
      data-testid="connector-picker-trigger"
      aria-busy={!controls.sourcesReady}
      className="h-[34px] rounded-lg bg-[#1a2436] px-3.5 text-[12.5px] font-semibold shadow-none hover:bg-[#2c3a52]"
      disabled={!controls.sourcesReady}
      onClick={() => setConnectorPickerOpen(true)}
    >
      <Plus className="size-[13px]" />
      Add connector
    </Button>
  )

  return (
    <>
      <Card className="gap-0 rounded-xl border-slate-200 py-0 shadow-none">
        <CardContent className="space-y-4 p-5">
          <header className="flex items-start gap-3.5">
            <div className="min-w-0 flex-1">
              <h1 className="text-[17px] font-[650] text-slate-900">Connectors</h1>
              <p className="mt-0.5 text-[13px] text-slate-500">
                Sources for this workspace. Files sync into{' '}
                {vaultName ? <strong className="font-semibold text-slate-700">{vaultName}</strong> : 'the paired workspace Vault'}
                {' '}— the paired workspace Vault — automatically. Each source syncs and runs independently.
              </p>
            </div>
            {controls.canManage && addConnectorButton()}
          </header>
          <ConnectorAnalysisPanel
            apiClient={api}
            orgId={orgId}
            workspaceId={workspaceId}
            onRefreshDocuments={refresh}
            // cm-coding: A progress card can load before the feed definition
            // required by Configure. Hide the action until both queries are
            // ready; accepting an earlier click would have no complete provider
            // settings to edit and could silently do nothing.
            onConfigureFeed={controls.sourcesReady ? feedId => {
              const feed = controls.feeds.find(item => item.feedId === feedId)
              if (feed) controls.editFeed(feed)
            } : undefined}
            onDisconnectFeed={(feedId, sourceName) => controls.disconnectFeed({
              feedId,
              remoteFolderName: sourceName,
            })}
            onPullRecords={setPullFeedId}
            canConfigureByProvider={controls.canDefineQuery}
            canResetWorkspaceByProvider={controls.canResetWorkspace}
            queryAuthorityByProvider={controls.queryAuthority}
            pullableFeedIds={pullCatalog.data?.sources.map(source => source.feedId) ?? []}
            onRefreshSyncedRecords={refreshSyncedRecords}
            onSyncFeed={syncFeed}
            pendingActions={pendingActions}
          />
          <div className="flex gap-2.5 rounded-[10px] border border-[#eef1f5] bg-slate-50 px-3.5 py-[11px] text-[12.5px] leading-[1.55] text-slate-500">
            <Info className="mt-px size-3.5 shrink-0" />
            <p>
              The Vault only stores what connectors deliver. Synced folders there are read-only and labeled{' '}
              <strong className="font-semibold text-slate-700">Live from connector — managed in Connectors</strong>;
              adding, configuring, syncing, and disconnecting all happen on this page. Personal and departmental
              Vaults aren&apos;t paired to a workspace, so they have no connectors — upload only.
            </p>
          </div>
        </CardContent>
      </Card>
      <ManageRecordsDialog
        api={api}
        orgId={orgId}
        workspaceId={workspaceId}
        feedId={pullFeedId}
        open={Boolean(pullFeedId)}
        onOpenChange={open => { if (!open) setPullFeedId(undefined) }}
        onQueued={refresh}
        onPendingChange={setPendingAction}
      />
      <ConnectorPickerDialog
        open={connectorPickerOpen}
        onOpenChange={setConnectorPickerOpen}
        vaultName={vaultName}
        sourceActions={controls.sourceActions}
      />
      {controls.overlays}
    </>
  )
}

export default function WorkspaceConnectorsPage() {
  const api = useServices().getClient()
  const { selectedOrganizationId, selectedWorkspaceId, currentWorkspace } = useAuth()
  const orgId = selectedOrganizationId ?? ''
  const workspaceId = selectedWorkspaceId ?? ''
  // A connector administrator without membership cannot read workspace
  // config; their discovered pairing is the proof this page applies.
  const isConnectorAdminHere = currentWorkspace?.role === 'connector-admin'
  const { connectorIntakeEnabled, loading: configLoading } = useWorkspaceConfig(
    orgId,
    workspaceId,
    !isConnectorAdminHere,
  )
  const pairing = useQuery({
    queryKey: ['workspace-vault-pairing', orgId, workspaceId],
    queryFn: () => getWorkspaceVaultPairing(api, orgId, workspaceId),
    enabled: Boolean(orgId && workspaceId),
  })

  if (!orgId || !workspaceId) return <Navigate to="/" replace />
  if (!configLoading && !connectorIntakeEnabled && !isConnectorAdminHere) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="p-4 sm:p-6">
      {pairing.data ? (
        <WorkspaceConnectorManager orgId={orgId} workspaceId={workspaceId} pairing={pairing.data} />
      ) : !pairing.isPending ? (
        <Card className="border-slate-200"><CardContent className="p-5 text-sm text-slate-500">
          This workspace has no connectors. Upload documents directly from its dashboard.
        </CardContent></Card>
      ) : null}
    </div>
  )
}
